import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { OrderStatus } from "@/hooks/use-orders";
import { AlertCircle, Check, X } from "lucide-react";
import { useState } from "react";

interface OrderStatusUpdateProps {
	orderId: string;
	currentStatus: OrderStatus;
	onStatusUpdate: (data: {
		orderId: string;
		status: OrderStatus;
		trackingNumber?: string;
		carrier?: string;
		notes?: string;
	}) => void;
	onAcceptOrder?: () => void;
	onRejectOrder?: () => void;
	isUpdating: boolean;
}

export function OrderStatusUpdate({
	orderId,
	currentStatus,
	onStatusUpdate,
	onAcceptOrder,
	onRejectOrder,
	isUpdating,
}: OrderStatusUpdateProps) {
	const [status, setStatus] = useState<OrderStatus>(currentStatus);
	const [trackingNumber, setTrackingNumber] = useState("");
	const [carrier, setCarrier] = useState("");
	const [notes, setNotes] = useState("");
	const [error, setError] = useState("");

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setError("");

		if (status === "shipped" && !trackingNumber) {
			setError(
				"Tracking number is required when marking an order as shipped"
			);
			return;
		}

		onStatusUpdate({
			orderId,
			status,
			trackingNumber: trackingNumber || undefined,
			carrier: carrier || undefined,
			notes: notes || undefined,
		});
	};

	const statusOptions: { value: OrderStatus; label: string }[] = [
		{ value: "pending", label: "Pending" },
		{ value: "received", label: "Received" },
		{ value: "processing", label: "Processing" },
		{ value: "shipped", label: "Shipped" },
		{ value: "delivered", label: "Delivered" },
		{ value: "cancelled", label: "Cancelled" },
	];

	// Filter out statuses based on current status to prevent going backward
	// except for cancelled which can be set from any status
	const getAvailableStatuses = () => {
		const statusIndex = statusOptions.findIndex(
			(option) => option.value === currentStatus
		);

		if (statusIndex === -1) return statusOptions;

		return statusOptions.filter((option, index) => {
			return index >= statusIndex || option.value === "cancelled";
		});
	};

	// For pending orders, show accept/reject buttons
	if (currentStatus === "pending" && onAcceptOrder && onRejectOrder) {
		return (
			<div className="space-y-4">
				<Alert>
					<AlertCircle className="h-4 w-4" />
					<AlertDescription>
						This order needs your approval before processing.
					</AlertDescription>
				</Alert>

				<div className="flex space-x-4">
					<Button
						variant="default"
						className="flex-1"
						onClick={onAcceptOrder}
						disabled={isUpdating}>
						<Check className="mr-2 h-4 w-4" />
						Accept Order
					</Button>
					<Button
						variant="destructive"
						className="flex-1"
						onClick={onRejectOrder}
						disabled={isUpdating}>
						<X className="mr-2 h-4 w-4" />
						Reject Order
					</Button>
				</div>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			{error && (
				<Alert variant="destructive">
					<AlertCircle className="h-4 w-4" />
					<AlertDescription>{error}</AlertDescription>
				</Alert>
			)}

			<div className="space-y-2">
				<Label htmlFor="status">Order Status</Label>
				<Select
					value={status}
					onValueChange={(value) => setStatus(value as OrderStatus)}>
					<SelectTrigger id="status">
						<SelectValue placeholder="Select new status" />
					</SelectTrigger>
					<SelectContent>
						{getAvailableStatuses().map((option) => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{(status === "shipped" || status === "delivered") && (
				<>
					<div className="space-y-2">
						<Label htmlFor="trackingNumber">
							Tracking Number
							{status === "shipped" && (
								<span className="text-red-500">*</span>
							)}
						</Label>
						<Input
							id="trackingNumber"
							value={trackingNumber}
							onChange={(e) => setTrackingNumber(e.target.value)}
							placeholder="Enter tracking number"
							required={status === "shipped"}
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="carrier">Shipping Carrier</Label>
						<Input
							id="carrier"
							value={carrier}
							onChange={(e) => setCarrier(e.target.value)}
							placeholder="e.g. UPS, FedEx, USPS"
						/>
					</div>
				</>
			)}

			<div className="space-y-2">
				<Label htmlFor="notes">Notes (Optional)</Label>
				<Textarea
					id="notes"
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
					placeholder="Add any additional information"
					rows={3}
				/>
			</div>

			<Button
				variant="accent"
				type="submit"
				className="w-full"
				disabled={isUpdating}>
				{isUpdating ? "Updating..." : "Update Order Status"}
			</Button>
		</form>
	);
}
